/* eslint-disable react/require-default-props */
/* eslint-disable consistent-return */
import React, { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';

export interface AudioWaveRecorderHandle {
  stop: () => Promise<Blob | null>;
  cancel: () => void;
}

const WAVE_THEMES = {
  dark: {
    waveColor: '#5E6D83',
    progressColor: '#FFFFFF',
  },
  white: {
    waveColor: '#CED4DA',
    progressColor: '#6C757D',
  },
};

interface Props {
  variant?: 'dark' | 'white';
  height?: number;
}

export const AudioWaveRecorder = forwardRef<AudioWaveRecorderHandle, Props>(
  ({ variant = 'white', height = 40 }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const recordRef = useRef<any>(null);

    const theme = WAVE_THEMES[variant];

    useEffect(() => {
      if (!containerRef.current) return;

      const ws = WaveSurfer.create({
        container: containerRef.current,
        waveColor: theme.waveColor,
        progressColor: theme.progressColor,
        barWidth: 2,
        barGap: 3,
        barRadius: 30,
        height: 40,
        interact: false,
        autoCenter: true,
      });

      const record = ws.registerPlugin(
        RecordPlugin.create({
          scrollingWaveform: true,
          renderRecordedAudio: false,
        }),
      );

      record.startRecording().catch((err) => console.error('Erro microfone:', err));

      record.on('record-progress', (duration: number) => {
        const MAX_DURATION = 120000;

        if (duration >= MAX_DURATION) {
          record.stopRecording();
          console.warn('Limite de 2 minutos atingido.');
        }
      });

      wavesurferRef.current = ws;
      recordRef.current = record;

      return () => {
        if (ws) {
          record.stopMic();
          ws.destroy();
        }
      };
    }, [variant, height]);

    useImperativeHandle(ref, () => ({
      async stop() {
        if (!recordRef.current) return null;

        return new Promise((resolve) => {
          recordRef.current.once('record-end', (blob: Blob) => {
            const audioFile = new File([blob], 'audio.webm', { type: 'audio/webm' });
            resolve(audioFile);
          });

          recordRef.current.stopRecording();
        });
      },
      cancel() {
        if (recordRef.current && recordRef.current.isRecording()) {
          recordRef.current.stopRecording();
        }
      },
    }));

    return (
      <div className="audio-waveform-container pl-3 d-flex overflow-hidden align-items-center flex-grow-1">
        <div ref={containerRef} className="w-100" />
      </div>
    );
  },
);
